import { computeOneOffCosts, fmt, fmtMoney, type SeparationState } from '@integrate/core';

/*
  One-off separation costs: the severance populations, and the hires.

  ONE COMPONENT because two panes print it — the separation summary and the phase bridge — and in the
  app they are two copies of the same twelve-line template (orgchart.html:7996-8009 and :7841-7854). Two
  copies of a table is two things to keep in step, and the whole class of defect this milestone keeps
  finding is two surfaces of one tool quietly disagreeing about a number.

  THE TWO HIRING ROWS STAY SPLIT. Every other figure on both pages counts existing people only — the
  stage bridge adds no Remain-side build-up — so the RemainCo hires a design commits to appear nowhere
  else. Collapsing them into one "build-ups" row would leave the reader with no figure anywhere for
  what RemainCo is being invoiced.

  A row with nobody in it is dropped rather than shown as zero, which is the app's rule and the right
  one: this reads as a list of what is happening, and "0 people, €0" is not something happening. The
  three severance rows stay whatever their count, with an em dash for the cost, because their absence
  is itself the answer to "how many redundancies does this design make".
*/

interface Row {
  key: string;
  label: string;
  n: number;
  cost: number;
  /** Severance rows are printed at zero; hiring rows are not. */
  always: boolean;
}

export function OneOffTable({
  state,
  testId = 'oneoffs',
}: {
  state: SeparationState;
  /** Prefixed onto every testid, so two panes on screen at once stay addressable apart. */
  testId?: string;
}): JSX.Element {
  const O = computeOneOffCosts(state);

  if (O.n === 0) {
    return (
      <p className="muted" data-testid={`${testId}-none`}>
        No one-off costs yet — no redundancy populations marked, and no build-ups planned.
      </p>
    );
  }

  const rows: Row[] = [
    {
      key: 'nlr',
      label: 'No longer required — redundant at close (severance)',
      n: O.nlrN,
      cost: O.nlrSeverance,
      always: true,
    },
    {
      /*
        "people in post", because the flow tile beside this on both panes counts POSITIONS: its figure
        includes a Day 1 build-up cancelled at Day 2, which has nobody to sever. Core states that rule
        ("a cancelled build-up position has no incumbent") and until now no screen did, so a tile reading
        −5 sat above a row reading 4 with nothing reconciling them.
      */
      key: 'day2-exits',
      label: 'Carve-out Day 2 exits (severance) — people in post',
      n: O.d2N,
      cost: O.d2Severance,
      always: true,
    },
    {
      key: 'rightsize',
      label: 'RemainCo stranded rightsizings (severance)',
      n: O.strandedN,
      cost: O.strandedSeverance,
      always: true,
    },
    {
      /*
        Shown only once a budget exists: with retentionPct at 0 the engine emits zeros and a
        "€0 retention" row would change every pre-retention project's table for nothing. The people
        counted here are the d2/stranded people above — one person, two cheques, counted once in
        the total's n.
      */
      key: 'retention',
      label: 'Retention to exit (Day 2 exits + rightsizings)',
      n: O.retentionN,
      cost: O.retention,
      always: false,
    },
    {
      key: 'carve-hiring',
      label: 'Carve-out build-up hiring (new hires)',
      n: O.carveBuN,
      cost: O.carveHiring,
      always: false,
    },
    {
      key: 'remain-hiring',
      label: 'RemainCo build-up hiring (new hires)',
      n: O.remainBuN,
      cost: O.remainHiring,
      always: false,
    },
  ].filter((r) => (r.key === 'retention' ? r.cost > 0 : r.always || r.n > 0));

  return (
    <table className="data-table" data-testid={testId}>
      <thead>
        <tr>
          <th>Population</th>
          <th className="num">People</th>
          <th className="num">One-off cost</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} data-testid={`${testId}-${r.key}`} data-n={r.n} data-cost={r.cost}>
            <td>{r.label}</td>
            <td className="num">{fmt(r.n)}</td>
            {/* An em dash where the app puts one: nobody in the population, so no cost to state. */}
            <td className="num">{r.n ? fmtMoney(r.cost) : '—'}</td>
          </tr>
        ))}
        <tr style={{ fontWeight: 700 }} data-testid={`${testId}-total`} data-n={O.n} data-cost={O.total}>
          <td>Total one-off</td>
          <td className="num">{fmt(O.n)}</td>
          <td className="num">{fmtMoney(O.total)}</td>
        </tr>
      </tbody>
    </table>
  );
}
